import * as Sentry from '@sentry/react';
import { ApiHttpError } from '@/lib/apiHttpError';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// Mutations can opt INTO the global error toast even when they define their own
// onError handler. Mirrors the React Query idiom of using mutation.meta to flag
// cross-cutting behaviors.
declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      alsoShowGlobalErrorToast?: boolean;
    };
  }
}

// 4xx statuses that we still want in Sentry because they signal infra/security
// problems, not normal user validation failures.
const SENTRY_REPORTABLE_4XX = new Set([401, 403, 429]);

export function shouldReportToSentry(status: number): boolean {
  return status >= 500 || SENTRY_REPORTABLE_4XX.has(status);
}

// Whether a mutation error should be captured to Sentry, independent of whether the
// mutation defines its own onError. HTTP errors are filtered by status (5xx + auth/rate-limit);
// non-HTTP errors (network failures, unexpected throws) are always worth reporting.
export function shouldCaptureMutationErrorToSentry(err: unknown): boolean {
  if (err instanceof ApiHttpError) return shouldReportToSentry(err.response.status);
  return err instanceof Error;
}

// Build a human-readable description from a Huma RFC 9457 problem-details body.
// Prefers per-field validation errors, then top-level detail, then the raw message.
export function describeApiError(err: ApiHttpError): string {
  if (err.errors && err.errors.length > 0) {
    return err.errors
      .map((e) => (e.location ? `${e.location}: ${e.message}` : e.message))
      .join('; ');
  }
  if (err.detail) {
    return err.detail;
  }
  return `${err.message} (${err.response.status})`;
}

// Global mutation error side effects (Sentry + toast). Runs for every mutation,
// regardless of whether the mutation provides its own onError — `defaultOptions.mutations.onError`
// would be REPLACED by per-mutation onError, so this lives on the MutationCache instead.
//
// Sentry capture is centralized here and decoupled from per-mutation onError: server/auth/
// rate-limit and non-HTTP errors are always reported so a mutation that adds local error
// handling can't accidentally drop reporting. Toast behavior is the part that stays scoped —
// if a mutation has its own onError we skip the global toast (its custom toast is more
// specific), unless it opts in via `meta: { alsoShowGlobalErrorToast: true }`.
const mutationCache = new MutationCache({
  onError: (err, _variables, _context, mutation) => {
    if (!(err instanceof Error)) return;

    const hasOwnOnError = mutation.options.onError !== undefined;
    const optedIn = mutation.options.meta?.alsoShowGlobalErrorToast === true;
    const shouldShowToast = !hasOwnOnError || optedIn;

    if (err instanceof ApiHttpError) {
      const status = err.response.status;

      // 4xx other than auth/rate-limit are expected client errors (validation,
      // conflict, etc.) and should not pollute Sentry. See ENR-1528.
      if (shouldCaptureMutationErrorToSentry(err)) {
        Sentry.captureException(err, {
          tags: { type: 'mutation', http_status: status },
          extra: { url: err.response.url },
        });
      }

      if (!shouldShowToast) return;

      const isAdminApi = err.response.url.includes('/admin/');
      const copyLogsAction = isAdminApi
        ? {
            action: (
              <Button
                variant="enrich_secondary"
                onClick={() =>
                  navigator.clipboard.writeText(JSON.stringify(err.responseBody, null, 4))
                }
              >
                Copy logs
              </Button>
            ),
          }
        : {};

      const description = describeApiError(err);
      if (status >= 500) {
        toast.error('Unexpected error', { id: 'mutation-error', description, ...copyLogsAction });
        return;
      }
      toast.error('Error processing your request', {
        id: 'mutation-error',
        description,
        ...copyLogsAction,
      });
      return;
    }

    // Non-HTTP errors (network failures, etc.)
    if (shouldCaptureMutationErrorToSentry(err)) {
      Sentry.captureException(err, {
        tags: { type: 'mutation', error_type: 'non_http' },
      });
    }
    if (shouldShowToast) {
      toast.error('Unexpected error', {
        id: 'mutation-error',
        description: `Error: "${err.message}"`,
      });
    }
  },
});

const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      throwOnError: (error) => {
        // Don't throw 401s - let components handle auth failures
        if (error instanceof ApiHttpError && error.response.status === 401) {
          return false;
        }

        // Capture 5xx server errors to Sentry (these are unexpected)
        if (error instanceof ApiHttpError && error.response.status >= 500) {
          Sentry.captureException(error, {
            tags: {
              type: 'query',
              http_status: error.response.status,
            },
            extra: {
              url: error.response.url,
            },
          });
        }

        return true;
      },
      retry: (failureCount, error) => {
        if (error instanceof ApiHttpError) {
          // Don't retry 4xx errors (client errors, including auth)
          if (error.response.status >= 400 && error.response.status < 500) {
            return false;
          }
        }
        console.warn(`Query error ${failureCount}: ${error.message || error}`);
        switch (failureCount) {
          case 1:
            toast.info('Connecting...', { id: 'retry-warning', duration: 4000 });
            break;
          case 2:
            toast.warning('Connection error. Retrying...', { id: 'retry-warning', duration: 8000 });
            break;
          case 3:
            toast.warning('Attempting to reconnect...', { id: 'retry-warning', duration: 10000 });
            break;
          default:
            toast.dismiss('retry-warning');
            break;
        }
        return failureCount < 4;
      },
    },
    mutations: {
      retry: false, // Mutations are not retried by React Query (retry logic is in useApiFetch for 401s)
      // Global error side effects (Sentry, toast) live on `mutationCache` above so they
      // fire for every mutation, including those with their own per-mutation `onError`.
    },
  },
});

export function EnrichQueryClientProvider({ children }: { children: React.ReactNode }) {
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
